import { ConfirmProvider } from "material-ui-confirm";
import { SnackbarProvider } from "notistack";
import { SWRConfig } from "swr";

import React, { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license-pro";

import ColorModeContext from "@/context/ColorModeContext";
import UserInfoContext from "@/context/UserInfoContext";
import { GlobalStoreContext } from "@/context/globalStoreContext";
import useColorMode from "@/hooks/useColorMode";
import useUserInfo from "@/hooks/useUserInfo";

import Authorize from "./components/Authorize";
import GuidesLayout from "./components/GuidesLayout";
import { Layout } from "./components/Layout";
import NotFound from "./components/NotFound";
import AccountLayout from "./components/SettingLayout";
import { AlertParamsContext } from "./context/AlertParamsContext";
import useGlobalStore from "./hooks/useGlobalStore";
import MyAlert from "./pages/Alert";
import AllRoutesPage from "./pages/AllRoutes";
import AuthDevV3 from "./pages/AuthDevV3";
import ContactUs from "./pages/Contact";
import CreditCard from "./pages/CreditCard";
import CreditCardDetail from "./pages/CreditCard/Detail";
import DealsFlight from "./pages/DealsFlight";
import DealsRoute from "./pages/DealsRoute";
import Email from "./pages/Email";
import FlightHome from "./pages/FlightHome";
import FlightSearch from "./pages/FlightSearch";
import Guides from "./pages/Guides";
import AlertsGuide from "./pages/Guides/Alerts";
import AmexTransfer from "./pages/Guides/AmexTransfer";
import BiltTransfer from "./pages/Guides/BiltTransfer";
import CapitalOneTransfer from "./pages/Guides/CapitalOneTransfer";
import ChaseTransfer from "./pages/Guides/ChaseTransfer";
import CitiTransfer from "./pages/Guides/CitiTransfer";
import HotelsGuide from "./pages/Guides/Hotels";
import FlightsGuide from "./pages/Guides/RealTime";
import ReferralProgram from "./pages/Guides/ReferralProgram";
import RoutesGuide from "./pages/Guides/Routes";
import Hotel from "./pages/Hotel";
import HotelCalendar from "./pages/HotelCalendar";
import LiveSeatMap from "./pages/LiveSeatMap";
import MegaSearch from "./pages/MegaSearch";
import OurServices from "./pages/OurServices";
import Plan from "./pages/Plan";
import PreferredPrograms from "./pages/PreferredPrograms";
import TwoThousandsPrice from "./pages/Price/TwoThousands";
import PrivacyTerm from "./pages/PrivacyTerm";
import Referral from "./pages/Referral";
import RouteDetail from "./pages/RouteDetail";
import RoutesPage from "./pages/Routes";
import RoutesFlights from "./pages/RoutesFlights";
import { FilterData } from "./pages/RoutesFlights/components/FilterBar/typing";
import SignIn from "./pages/SignIn/SignInPage";
import SignInPassword from "./pages/SignInPassword";
import TermsOfUse from "./pages/TermsOfUse";
import VerifyRequest from "./pages/VerifyRequest";

LicenseInfo.setLicenseKey(
  "e1ba2519f58e764ab2005da6361c8e46Tz05NTM5NSxFPTE3NTQxNTMwMzYwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLFBWPWluaXRpYWwsS1Y9Mg=="
);

function App() {
  const { mode, colorMode, isDarkMode } = useColorMode();
  const { userInfoContextInit, userInfoContextValue } = useUserInfo();

  const { store } = useGlobalStore();

  const [alertParams, setAlertParams] = useState<Partial<FilterData>>();

  const theme = React.useMemo(
    () =>
      createTheme({
        breakpoints: {
          values: {
            xs: 0,
            sm: 700,
            md: 900,
            lg: 1200,
            xl: 1536,
          },
        },
        palette: { mode },
        typography: {
          button: {
            textTransform: "none",
          },
        },
        components: {
          MuiSlider: {
            styleOverrides: {
              valueLabel: ({ theme }) => {
                return {
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "12px",
                  color: mode === "dark" ? theme.palette.common.black : theme.palette.common.white,
                };
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: () => {
                return {
                  backgroundColor: mode === "dark" ? "#202124" : "#fff",
                  boxShadow:
                    mode === "dark"
                      ? "0 1px 2px 0 rgba(0,0,0,.3),0 2px 6px 2px rgba(0,0,0,.15)"
                      : "0 1px 2px 0 rgba(60,64,67,.3),0 2px 6px 2px rgba(60,64,67,.15)",
                  color: mode === "dark" ? "#fff" : "#424242",
                  fontSize: 14,
                  padding: "16px",
                };
              },
              arrow: () => {
                return {
                  color: mode === "dark" ? "#202124" : "#fff",
                };
              },
            },
          },
        },
      }),
    [mode]
  );

  useEffect(() => {
    document.body.classList.remove(isDarkMode ? "mode-light" : "mode-dark");
    document.body.classList.add(isDarkMode ? "mode-dark" : "mode-light");
  }, [isDarkMode]);

  useEffect(() => {
    userInfoContextInit(store.getUserInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SWRConfig>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ConfirmProvider>
            <GlobalStoreContext.Provider value={store}>
              <UserInfoContext.Provider value={userInfoContextValue}>
                <ColorModeContext.Provider value={colorMode}>
                  <SnackbarProvider maxSnack={3}>
                    <AlertParamsContext.Provider
                      value={{
                        alertParams,
                        setAlertParams,
                      }}
                    >
                      <Routes>
                        <Route path="/" element={<Layout />}>
                          <Route index element={<FlightHome />} />
                          <Route path="/r/*" element={<FlightHome />} />
                          <Route path="/flight" element={<FlightSearch />} />
                          <Route path="/mega" element={<MegaSearch />} />
                          <Route path="/privacy-policy" element={<PrivacyTerm />} />
                          <Route path="/terms-of-use" element={<TermsOfUse />} />
                          <Route path="/auth" element={<AuthDevV3 />} />
                          <Route path="/signin" element={<SignIn />} />
                          <Route path="/verify-request" element={<VerifyRequest />} />
                          <Route path="/password" element={<SignInPassword />} />
                          <Route path="/alert" element={<MyAlert />} />
                          <Route
                            path="/setting"
                            element={
                              <Authorize>
                                <AccountLayout />
                              </Authorize>
                            }
                          >
                            <Route path="/setting/plan" element={<Plan />} />
                            <Route path="/setting/email" element={<Email />} />
                            <Route path="/setting/referral" element={<Referral />} />
                            <Route
                              path="/setting/preferred-programs"
                              element={<PreferredPrograms />}
                            />
                          </Route>
                          <Route path="/alert-detail" element={<FlightSearch />} />
                          <Route path="/ad" element={<FlightSearch />} />
                          <Route path="/routes" element={<RoutesPage />} />
                          <Route path="/routes-flights" element={<RoutesFlights />} />
                          <Route path="/alert-routes-flights" element={<RoutesFlights />} />
                          <Route path="/panorama" element={<RoutesPage />} />
                          <Route path="/all-routes" element={<AllRoutesPage />} />
                          <Route path="/route-detail" element={<RouteDetail />} />
                          <Route path="/hotels" element={<Hotel />} />
                          <Route path="/hotel/details" element={<HotelCalendar />} />
                          <Route path="/deals/routes" element={<DealsRoute />} />
                          <Route path="/deals/flight" element={<DealsFlight />} />
                          <Route path="/live-seat-map" element={<LiveSeatMap />} />
                          <Route path="/credit-card" element={<CreditCard />} />
                          <Route path="/credit-card/:id" element={<CreditCardDetail />} />
                          <Route path="/pro" element={<OurServices />} />
                          <Route path="/contact" element={<ContactUs />} />
                          <Route path="/faq" element={<ContactUs />} />
                          <Route path="/guides" element={<GuidesLayout />}>
                            <Route index element={<Guides />} />
                            <Route path="/guides/amextransfer" element={<AmexTransfer />} />
                            <Route path="/guides/bilttransfer" element={<BiltTransfer />} />
                            <Route path="/guides/chasetransfer" element={<ChaseTransfer />} />
                            <Route path="/guides/cititransfer" element={<CitiTransfer />} />
                            <Route
                              path="/guides/capitalonetransfer"
                              element={<CapitalOneTransfer />}
                            />
                            <Route path="/guides/flights" element={<FlightsGuide />} />
                            <Route path="/guides/routes" element={<RoutesGuide />} />
                            <Route path="/guides/alerts" element={<AlertsGuide />} />
                            <Route path="/guides/hotels" element={<HotelsGuide />} />
                            <Route path="/guides/referral" element={<ReferralProgram />} />
                          </Route>
                          <Route path="/api-price-fb" element={<TwoThousandsPrice />} />
                          <Route path="*" element={<NotFound />} />
                        </Route>
                      </Routes>
                    </AlertParamsContext.Provider>
                  </SnackbarProvider>
                </ColorModeContext.Provider>
              </UserInfoContext.Provider>
            </GlobalStoreContext.Provider>
          </ConfirmProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </SWRConfig>
  );
}

export default App;
